import * as React from 'react';
import { TextField, Box, FormHelperText, Tooltip, IconButton } from '@mui/material';
import Info from '@material-ui/icons/Info';

export interface PasswordConfirmationProps {
    id?: string,
    value?: string,
    label?: string,
    confirmLabel?: string,
    placeholder?: string,
    confirmPlaceholder?: string,
    needToConfirmText: string,
    info?: string,
    infoAsText?: boolean,
    stacked?: boolean,
    minLength?: number,
    onChange: (value: string | undefined, confimed: boolean) => void,
}

export const PasswordConfirmationField = (props: PasswordConfirmationProps) => {
    const {
        id,
        value,
        label,
        confirmLabel,
        placeholder,
        confirmPlaceholder,
        info,
        infoAsText = false,
        needToConfirmText,
        stacked,
        minLength = 3,
        onChange,
    } = props;

    const [confirmPassword, setConfirmPassword] = React.useState(value);
    const [infoToolTip, setInfoToolTip] = React.useState(false);

    const isTooShort = value && value.length > 0 && value.length < minLength;
    const isMatching = value === confirmPassword;
    const isValid = !isTooShort && isMatching;

    const onPasswordChange = (password: string) => {
        const newPassword = password ? password : undefined;
        const newIsTooShort = newPassword && newPassword.length > 0 && newPassword.length < minLength;
        const newIsValid = !newIsTooShort && newPassword === confirmPassword;
        onChange(newPassword, newIsValid);
    };

    const onConfirmPasswordChange = (newConfirmPassword: string) => {
        const newIsTooShort = value && value.length > 0 && value.length < minLength;
        const newIsValid = !newIsTooShort && value === newConfirmPassword;
        onChange(value, newIsValid);
        setConfirmPassword(newConfirmPassword);
    };

    return (
        <>
            <Box display="flex" gap={2} alignItems="flex-start" flexDirection={stacked ? 'column' : 'row'}>
                <Box flex={stacked ? undefined : 5} width="100%">
                    <TextField
                        id={id ? `${id}-input` : undefined}
                        type="password"
                        label={label}
                        placeholder={placeholder}
                        autoComplete="new-password"
                        error={isTooShort || false}
                        value={value}
                        onChange={e => onPasswordChange(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                    />
                    {isTooShort && (
                        <FormHelperText error>
                            Password must be at least {minLength} characters
                        </FormHelperText>
                    )}
                </Box>
                <Box flex={stacked ? undefined : 5} width="100%">
                    <TextField
                        id={id ? `${id}-confirmation-input` : undefined}
                        type="password"
                        label={confirmLabel}
                        placeholder={confirmPlaceholder}
                        autoComplete="new-password"
                        error={(value && confirmPassword !== value) || false}
                        value={confirmPassword}
                        onChange={e => onConfirmPasswordChange(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                    />
                    {value && confirmPassword !== value && (
                        <FormHelperText error>{needToConfirmText}</FormHelperText>
                    )}
                </Box>
                { info && !infoAsText &&
                    <Box flex={stacked ? undefined : 1}>
                        <Tooltip
                            title={info}
                            placement="bottom"
                            open={infoToolTip}
                            onClose={() => setInfoToolTip(false)}
                            onOpen={() => setInfoToolTip(true)}
                        >
                            <IconButton size="small" onClick={() => setInfoToolTip(!infoToolTip)}>
                                <Info id="passwordInfoIcon"/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                }
            </Box>
            { info && infoAsText &&
                <FormHelperText sx={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--spacer-XS)' }}>
                    {info}
                </FormHelperText>
            }
        </>
    );
}
